// @flow

import React from 'react';
import bemify from 'bemify-js';

import { StaticImage } from '@zapier/common-components';

import Link from 'app/common/components/Link';
import MessageBar from 'app/common/components/MessageBar';
import Tag from 'app/common/components/Tag';
import { Heading2 } from 'app/common/components/Heading';
import 'app/login/components/SimplePromo.scss';

import type { Node } from 'react';

import type { KindType } from 'app/common/types';

const bem = bemify('simple-unauthed-promo');

type CtaLinkProps = {
  children: Node,
  href: string,
};
const CtaLink = (props: CtaLinkProps) => {
  return (
    <Link className={bem('__cta')} href={props.href}>
      {props.children}
    </Link>
  );
};

type SimplePromoFullProps = {
  children: Node,
  className?: string,
  ctaHref?: string,
  ctaText?: string,
  imgAlt?: string,
  imgSrc?: string,
  header?: Node,
  kind?: KindType,
  tag?: string,
  title: string | Node,
};
const SimplePromoFull = (props: SimplePromoFullProps) => {
  const tag = !props.tag ? null : (
    <Tag className={bem('__tag')} kind="success">
      {props.tag}
    </Tag>
  );
  const staticImg = !(props.imgSrc && props.imgAlt) ? null : (
    <StaticImage
      alt={props.imgAlt}
      className={bem('__image')}
      src={props.imgSrc || ''}
    />
  );
  const header = props.header || (
    <header className={bem('__header')}>
      {staticImg}
      {tag}
      <Heading2 className={bem('__title')}>{props.title}</Heading2>
    </header>
  );

  const footer = !(props.ctaHref && props.ctaText) ? null : (
    <footer className={bem('__footer')}>
      <CtaLink href={props.ctaHref}>{props.ctaText}</CtaLink>
    </footer>
  );

  return (
    <article className={bem(props.kind, props.className)}>
      {header}
      <div className={bem('__content')}>{props.children}</div>
      {footer}
    </article>
  );
};

type SimplePromoCondensedProps = {
  condensedContent?: Node,
  condensedIcon?: string,
  ctaHref?: string,
  ctaText?: string,
  tag?: string,
  title: string | Node,
};
const SimplePromoCondensed = (props: SimplePromoCondensedProps) => {
  const tag = !props.tag ? null : (
    <Tag className={bem('__mobile-tag')} kind="promo">
      {props.tag}
    </Tag>
  );
  const link = !(props.ctaHref && props.ctaText) ? null : (
    <CtaLink href={props.ctaHref}>{props.ctaText}</CtaLink>
  );
  return (
    <MessageBar icon={props.condensedIcon} mainKind="info">
      <Heading2 className={bem('__mobile-title')}>
        {props.title}
        {tag}
      </Heading2>
      <div>
        {props.condensedContent} {link}
      </div>
    </MessageBar>
  );
};

export type SimplePromoProps = {
  children: Node,
  className?: string,
  condensedContent?: Node,
  condensedIcon?: string,
  ctaHref?: string,
  ctaText?: string,
  imgAlt?: string,
  imgSrc?: string,
  header?: Node,
  isCondensed?: boolean,
  kind?: KindType,
  tag?: string,
  title: string | Node,
};
const SimplePromo = (props: SimplePromoProps) => {
  return props.isCondensed ? (
    <SimplePromoCondensed {...props} />
  ) : (
    <SimplePromoFull {...props} />
  );
};

export default SimplePromo;
